// Infinite-query hook for a channel's message history.
//
// Pages are loaded newest-first (the server's natural sort). The cursor is
// the oldest message id in the current batch, passed as `before` to fetch
// the next (older) page. `fetchNextPage` therefore means "load older".
//
// The caller (useChat) reverses each page before merging so the UI always
// sees oldest→newest order.

import { useInfiniteQuery } from '@tanstack/react-query';
import { useClientApis } from '../../hooks/ClientApisProvider.js';
import { commsKeys } from '../queryKeys.js';

export const HISTORY_QUERY_KEY = commsKeys.history;

export function useChannelHistory(
  siteId: string | null,
  channelId: string | null,
  options?: { enabled?: boolean },
) {
  const { fetchChannelHistory } = useClientApis().comms;
  return useInfiniteQuery({
    queryKey: HISTORY_QUERY_KEY(siteId ?? '', channelId ?? ''),
    queryFn: ({ pageParam, signal }) =>
      fetchChannelHistory(siteId!, channelId!, pageParam as string | undefined, signal),
    initialPageParam: undefined as string | undefined,
    // The cursor for the next (older) page is the id of the oldest message
    // in the current page. The server returns newest-first, so that's the
    // last element.
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore) return undefined;
      return lastPage.messages.at(-1)?.id;
    },
    // `options.enabled` lets the caller disable the fetch entirely for a
    // not-yet-persisted (virtual) DM — the backend 404s a channel that
    // doesn't exist yet, and there's nothing to fetch anyway.
    enabled: (options?.enabled ?? true) && !!siteId && !!channelId,
    staleTime: Infinity,
    // Always re-fetch the first page on mount so that messages received while
    // the user was away are picked up when they return to the comms page.
    // staleTime: Infinity still prevents mid-session churn; this only fires
    // when the component mounts (channel opened / page re-entered).
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
}
